import { imageFromEnv } from "@p0security/integrations-gcloud-shared/mcp/servers";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { LookerAiIconUrl } from "./icon.ts";
import { lookerAiMcpTools } from "./mcp-tools.ts";

// The connector creates the grant's Looker service account and its key before
// the grant completes. A key that hasn't reached Looker yet fails the
// Toolbox's sign-in at launch, not a tool call. These waits are guesses until
// the live test (W1) measures them.
const lookerAiPropagationHint = propagationHint({
  resource: "Looker",
  normalWait: "5 to 15 seconds",
  maxWait: "30 seconds",
  pollInterval: "5 seconds",
});

const prefixArgs = ["looker-ai", "model"];

const accessPrompt = `Request access to a LookML model in a Looker instance by calling with argv:

  [<instance>, <model>, <level>, "--reason", <reason>]

where:

  - instance: The Looker instance, as installed in P0. Use the list tool to
    enumerate them.
  - model: The LookML model's name. Use the list tool, passing the instance,
    to enumerate them.
  - level: 'read' to explore, query and run Looks and dashboards, or 'edit' to
    also save Looks and dashboards
  - reason: The reason access is needed

Each grant covers one model. Both levels list the same tools: Looker refuses an
edit tool on a read grant, and any call outside the granted model.
`;

/** Google's MCP Toolbox, pinned by the digest of its multi-arch index, with its
 * prebuilt Looker config. Agents see only the tools in
 * {@link lookerAiMcpTools}. The grant's Looker role decides what each call may
 * do, so a read grant can list an edit tool but Looker refuses the save.
 *
 * The connector stores each grant's Looker API key as
 * `<client_id>:<client_secret>`, both letters and digits, so splitting at the
 * colon gives back the two fields. The Toolbox pastes every env value into its
 * YAML config as is, so no value may hold a quote, a colon-space or a line
 * break: the connector refuses such a key, and the base URL is staged
 * normalized.
 */
export const lookerAiMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: "Looker",
  iconUrl: LookerAiIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name. Read it here, so this spec never
    // rebuilds the naming scheme.
    path: "{{ request.generated.secretName }}",
  },
  // The grant's Looker role decides what Looker allows.
  authorization: "upstream",
  transport: "stdio",
  tools: { type: "manual", tools: lookerAiMcpTools },
  container: {
    // The image's entrypoint is `/toolbox`. `--disable-version-check` stops it
    // asking GitHub for a newer release at every launch.
    args: ["--prebuilt", "looker", "--stdio", "--disable-version-check"],
    command: [],
    envs: {
      LOOKER_BASE_URL: "{{ request.generated.baseUrl }}",
      LOOKER_CLIENT_ID: '{{ credential.token | split: ":" | first }}',
      LOOKER_CLIENT_SECRET: '{{ credential.token | split: ":" | last }}',
      // The prebuilt config shows hidden models, Explores and fields unless
      // told not to.
      LOOKER_SHOW_HIDDEN_EXPLORES: "false",
      LOOKER_SHOW_HIDDEN_FIELDS: "false",
      LOOKER_SHOW_HIDDEN_MODELS: "false",
    },
    image: {
      image: imageFromEnv({
        env: "LOOKER_MCP_IMAGE",
        fallback:
          "us-central1-docker.pkg.dev/database-toolbox/toolbox/toolbox:1.14.0@sha256:dab91e25cbe7145d261df2978881c4e23e02238b42da3c72eea2df0a95aea797",
      }),
    },
  },
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: lookerAiPropagationHint,
  },
  list: {
    fields: {
      instance: {
        description: "Looker instance, as installed in P0",
        required: [],
      },
      model: {
        description: "LookML model, listed for one instance",
        required: ["instance"],
      },
      level: {
        description: "Level of access to grant on the model",
        required: [],
      },
    },
    prefixArgs,
  },
  location: {
    key: "instance",
    description: "Looker instance, as installed in P0",
  },
};

/** Whether {@link lookerAiMcp} can be added: only once a Looker instance is
 * installed, since without one there is nothing an agent could request.
 *
 * Keyed `looker`, the server's key in the catalog. The key is stored as each
 * added server's `definition.id`, so it must never change. */
export const lookerAiMcpServerAvailability = {
  looker: (installed: Record<string, NewConfig>): boolean =>
    installedIds("credential", {
      credential: installed["looker-ai"]?.credential ?? {},
    }).length > 0,
};
